import { useState } from "react";
import { motion } from "framer-motion";
import ParticleBackground from "../components/ParticleBackground";
import {
  Globe2,
  Code2,
  ShieldCheck,
  Smartphone,
  Database,
  Bot,
  ArrowRight,
  CheckCircle,
} from "lucide-react";

const services = [
  {
    title: "Website Development",
    icon: <Globe2 size={30} />,
    description:
      "Modern business websites, school websites, company profiles, landing pages and portfolio websites.",
    features: ["Responsive design", "SEO friendly", "Fast loading", "Contact forms"],
  },
  {
    title: "Custom Software Systems",
    icon: <Code2 size={30} />,
    description:
      "Tailor-made systems for schools, businesses, healthcare, bookings, records and dashboards.",
    features: ["Admin dashboards", "User roles", "Reports", "Database integration"],
  },
  {
    title: "Mobile Applications",
    icon: <Smartphone size={30} />,
    description:
      "Mobile-friendly systems and apps for organizations that need easy access on phones.",
    features: ["Android-ready", "Clean UI", "API integration", "User authentication"],
  },
  {
    title: "Database Systems",
    icon: <Database size={30} />,
    description:
      "Database design and management for secure, organized and scalable business information.",
    features: ["PostgreSQL", "SQL Server", "Data security", "Backups"],
  },
  {
    title: "AI-Powered Solutions",
    icon: <Bot size={30} />,
    description:
      "Smart assistants, AI insights, automation tools and intelligent digital workflows.",
    features: ["AI chatbot", "Smart insights", "Automation", "Decision support"],
  },
  {
    title: "Secure Digital Platforms",
    icon: <ShieldCheck size={30} />,
    description:
      "Secure systems with login, permissions, protected routes and role-based access.",
    features: ["JWT auth", "Role control", "Data protection", "Secure APIs"],
  },
];

function Services() {
  const [form, setForm] = useState({
    name: "",
    phone: "",
    email: "",
    service: "",
    message: "",
  });

  const handleChange = (e) => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  const selectService = (serviceTitle) => {
    setForm((prev) => ({ ...prev, service: serviceTitle }));

    setTimeout(() => {
      document
        .getElementById("booking-form")
        ?.scrollIntoView({ behavior: "smooth", block: "start" });
    }, 100);
  };

  const handleSubmit = (e) => {
    e.preventDefault();

    const whatsappMessage = encodeURIComponent(
      `Hello Eiffeltech, I want to book a service.

Name: ${form.name}
Phone: ${form.phone}
Email: ${form.email || "Not provided"}
Service: ${form.service}
Message: ${form.message}`
    );

    window.open(`https://wa.me/263716292923?text=${whatsappMessage}`, "_blank");
  };

  return (
    <section className="relative min-h-screen overflow-hidden pt-28 sm:pt-32 px-4">
      
      {/* PARTICLES BACKGROUND */}
      <div className="absolute inset-0 z-0">
        <ParticleBackground />
      </div>

      

      {/* MAIN CONTENT */}
      <div className="relative z-20 mx-auto  inset-0 z-10 max-w-7xl">

        {/* HEADER */}
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7 }}
          className="mx-auto max-w-4xl text-center"
        >
          <p className="text-xs font-bold uppercase tracking-[0.22em] text-cyan-400 sm:text-sm">
            Our Services
          </p>

          <h1 className="mt-4 text-2xl sm:text-4xl md:text-5xl lg:text-7xl font-black leading-tight">
            Book professional digital services
          </h1>

          <p className="mt-4 text-slate-300">
            Choose the service you need and send a booking request directly to Eiffeltech.
          </p>
        </motion.div>

        {/* SERVICES GRID */}
        <div className="mt-10 grid grid-cols-1 gap-4 sm:mt-14 sm:grid-cols-2 lg:grid-cols-3 lg:gap-8">
          {services.map((service, index) => (
            <motion.div
              key={service.title}
              initial={{ opacity: 0, y: 35 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: index * 0.08 }}
              whileHover={{ y: -6 }}
              className="border border-cyan-400/30 rounded-2xl p-5 bg-black/20 backdrop-blur-md"
            >
              <div className="mb-4 flex h-12 w-12 items-center justify-center rounded-2xl bg-cyan-400 text-slate-950">
                {service.icon}
              </div>

              <h2 className="text-lg font-black sm:text-2xl">
                {service.title}
              </h2>

              <p className="mt-2 text-slate-300">
                {service.description}
              </p>

              <div className="mt-5 space-y-2">
                {service.features.map((feature) => (
                  <p key={feature} className="flex items-center gap-2 text-slate-200">
                    <CheckCircle size={16} className="text-cyan-400" />
                    {feature}
                  </p>
                ))}
              </div>

              <button
                onClick={() => selectService(service.title)}
                className="mt-6 w-full rounded-full bg-cyan-400 px-5 py-3 text-sm font-bold text-black hover:bg-cyan-300"
              >
                Book This Service <ArrowRight size={17} />
              </button>
            </motion.div>
          ))}
        </div>

        {/* BOOKING */}
        <div
          id="booking-form"
          className="mt-14 grid grid-cols-1 gap-8 lg:grid-cols-2"
        >
          <div>
            <h2 className="text-2xl sm:text-4xl font-black text-white">
              Request a service
            </h2>

            <p className="mt-4 text-slate-300">
              Fill in your details and send via WhatsApp.
            </p>

            <div className="mt-6 border border-cyan-400/30 rounded-2xl p-5">
              <p className="text-cyan-400 font-bold">Contact</p>
              <p className="text-slate-300">+263716292923</p>
              <p className="text-slate-300">eiffel1@eiffeltech.co.zw</p>
            </div>
          </div>

          <form onSubmit={handleSubmit} className="space-y-3">
            <Input name="name" value={form.name} onChange={handleChange} placeholder="Your full name" />
            <Input name="phone" value={form.phone} onChange={handleChange} placeholder="Phone number" />
            <Input name="email" value={form.email} onChange={handleChange} placeholder="Email address" />

            <select
              name="service"
              value={form.service}
              onChange={handleChange}
              className="w-full rounded-lg border border-cyan-400/30 p-2 bg-black/30 text-white"
            >
              <option value="">Select service</option>
              {services.map((s) => (
                <option key={s.title} value={s.title}>
                  {s.title}
                </option>
              ))}
            </select>

            <textarea
              name="message"
              value={form.message}
              onChange={handleChange}
              rows="5"
              placeholder="Describe your request"
              className="w-full rounded-lg border border-cyan-400/30 p-2 bg-black/30 text-white"
            />

            <button className="w-full rounded-full bg-cyan-400 py-3 font-bold text-black">
              Send Booking Request
            </button>
          </form>
        </div>

      </div>
    </section>
  );
}

function Input(props) {
  return (
    <input
      {...props}
      className="w-full rounded-lg border border-cyan-400/30 p-2 bg-black/30 text-white focus:border-cyan-400 outline-none"
    />
  );
}

export default Services;
